import { blobToBytes } from '../../core/blob-bytes';
import { throwIfAborted, type ConvertContext, type Converter } from '../../core/converter';
import { EngineFailure } from '../../core/errors';
import { canonicalExtension, buildOutputName } from '../../core/filename';
import { extensionOf } from '../../core/file-detect';
import { documentFidelity } from '../../core/fidelity';
import { LOCAL_CONVERSION_COST } from '../../core/pricing';
import type { ConversionResult, ConverterCapabilities, ConvertOptions, FileMetadata } from '../../core/types';
import { hasUnbalancedFence, verifyDocumentOutput } from '../../core/verify';
import {
  defaultLoadPdfjs,
  type PdfDocumentLike,
  type PdfJsLoader,
  type PdfTextItem,
} from './pdfjs-loader';
import { normalizeNewlines } from './text-codec';

export const PDF_SOURCE_FORMATS = ['pdf'];
export const PDF_TARGET_FORMATS = ['txt', 'md'];

const MIME_BY_FORMAT: Record<string, string> = {
  txt: 'text/plain',
  md: 'text/markdown',
};

/** 本地提取的页数上限：超出说明内存风险，直接拒绝而不是把浏览器拖死。 */
export const PDF_MAX_LOCAL_PAGES = 500;

/** 词间空格推断阈值（PDF 用户单位）：相邻文本项水平间距超过该值时补一个空格。 */
const WORD_GAP_THRESHOLD = 1;

export interface PdfConverterDeps {
  loadPdfjs?: PdfJsLoader;
}

/**
 * 单页文本项拼装。
 *
 * pdf.js 返回的是「文本片段 + 定位矩阵」：`hasEOL` 标记换行，同一行内的片段是否相邻
 * 需要靠水平坐标判断 —— 否则 `Hello` 与 `World` 会被粘成 `HelloWorld`。
 * 这是启发式推断，保真度报告里会如实说明。
 */
export function pageItemsToText(items: PdfTextItem[]): string {
  const lines: string[] = [];
  let current = '';
  let lastEnd: number | null = null;

  for (const item of items) {
    const text = typeof item.str === 'string' ? item.str : '';
    const x = Array.isArray(item.transform) && typeof item.transform[4] === 'number' ? item.transform[4] : null;
    const width = typeof item.width === 'number' ? item.width : 0;

    if (text.length === 0) {
      if (item.hasEOL && current) {
        lines.push(current);
        current = '';
        lastEnd = null;
      }
      continue;
    }

    if (current && lastEnd !== null && x !== null && x - lastEnd > WORD_GAP_THRESHOLD) current += ' ';
    current += text;
    lastEnd = x !== null ? x + width : null;

    if (item.hasEOL) {
      lines.push(current);
      current = '';
      lastEnd = null;
    }
  }

  if (current) lines.push(current);
  return lines.join('\n');
}

/**
 * PDF → TXT / Markdown（纯本地，零上传）。
 *
 * 只提取**文本层**：扫描件（图片型 PDF）没有文本层，会抛出明确错误并引导到 AI 识别通道。
 * 不推断标题层级 —— 由文本猜结构必然出错，宁可不做（保真度报告里写明）。
 *
 * 执行位置：`main-thread`。pdf.js 自带 Worker 负责真正的解析，因此主线程不会被阻塞；
 * 这也避免在转换 Worker 里再套一层 Worker（嵌套 Worker 兼容性差且打包复杂）。
 */
export class PdfDocumentConverter implements Converter {
  private readonly loadPdfjs: PdfJsLoader;

  constructor(deps: PdfConverterDeps = {}) {
    this.loadPdfjs = deps.loadPdfjs ?? defaultLoadPdfjs;
  }

  capabilities(): ConverterCapabilities {
    return {
      id: 'document.pdf',
      category: 'document',
      sourceFormats: [...PDF_SOURCE_FORMATS],
      targetFormats: [...PDF_TARGET_FORMATS],
      local: true,
      cloud: false,
      defaultFidelity: 'L3',
      mode: 'local',
      runtime: 'main-thread',
      requiresUpload: false,
      estimatedCost: LOCAL_CONVERSION_COST,
    };
  }

  canConvert(source: FileMetadata, targetExtension: string): boolean {
    const sourceExtension = canonicalExtension(source.type.extension);
    const target = canonicalExtension(targetExtension);
    return PDF_SOURCE_FORMATS.includes(sourceExtension) && PDF_TARGET_FORMATS.includes(target);
  }

  async inspect(file: File): Promise<FileMetadata> {
    return {
      name: file.name,
      size: file.size,
      type: { extension: extensionOf(file.name), mime: file.type || 'application/pdf', category: 'document' },
    };
  }

  async convert(file: File, options: ConvertOptions = {}, context?: ConvertContext): Promise<ConversionResult> {
    const target = canonicalExtension(String(options.targetFormat ?? 'txt'));
    if (!PDF_TARGET_FORMATS.includes(target)) {
      throw new EngineFailure('UNSUPPORTED_CONVERSION', `PDF 引擎不支持输出 ${target} 格式`);
    }

    throwIfAborted(context);
    context?.onProgress?.({ progress: 10, stage: '打开 PDF' });
    const bytes = await blobToBytes(file);

    let document: PdfDocumentLike | null = null;
    try {
      const pdfjs = await this.loadPdfjs();
      document = await pdfjs.getDocument({ data: bytes }).promise;

      if (!Number.isFinite(document.numPages) || document.numPages <= 0) {
        throw new EngineFailure('MALFORMED_FILE', 'PDF 里没有任何页面');
      }
      if (document.numPages > PDF_MAX_LOCAL_PAGES) {
        throw new EngineFailure(
          'MEMORY_LIMIT',
          `PDF 有 ${document.numPages} 页，超过本地提取上限 ${PDF_MAX_LOCAL_PAGES} 页；请拆分后重试。`,
        );
      }

      const pageTexts: string[] = [];
      for (let index = 1; index <= document.numPages; index += 1) {
        throwIfAborted(context);
        const page = await document.getPage(index);
        const content = await page.getTextContent();
        pageTexts.push(pageItemsToText(content.items ?? []));
        context?.onProgress?.({
          progress: 10 + Math.round((index / document.numPages) * 70),
          stage: `提取第 ${index}/${document.numPages} 页`,
        });
      }

      const body = normalizeNewlines(pageTexts.join('\n\n')).replace(/[ \t]+\n/g, '\n').trim();
      if (!body) {
        throw new EngineFailure(
          'UNSUPPORTED_CONVERSION',
          '该 PDF 没有可提取的文本层（很可能是扫描件）：请改用「AI 识别」通道（需登录，每次 2 分）。',
        );
      }

      throwIfAborted(context);
      context?.onProgress?.({ progress: 88, stage: '校验' });
      const output = `${body}\n`;
      // 抽样比对必须与产物同一归一化口径：直接把原文按空白折叠，否则换行差异会误判为失败。
      const flattened = body.replace(/\s+/g, ' ');
      const sample = flattened.slice(0, 24).trim();
      const verification = verifyDocumentOutput({
        parsed: output,
        assertions: [
          { label: '产物非空', ok: output.trim().length > 0 },
          { label: `已提取 ${document.numPages} 页文本`, ok: pageTexts.some((text) => text.trim().length > 0) },
          { label: '首页文本出现在产物中', ok: sample.length === 0 || flattened.includes(sample) },
          // 提示性：正文里本就可能写着奇数个 ```，不该因此拦下产物
          { label: '无未闭合代码围栏', ok: !hasUnbalancedFence(output), advisory: true },
        ],
      });

      const notes: string[] = ['词间空格按文本片段位置间隔推断，可能与原版式略有差异'];
      if (document.numPages > 1) notes.push(`共 ${document.numPages} 页，按页码顺序拼接并以空行分隔`);
      if (target === 'md') notes.push('未推断标题层级：Markdown 产物仅承载正文文本');
      const fidelity = documentFidelity({ sourceExtension: 'pdf', targetExtension: target, notes });

      const mime = MIME_BY_FORMAT[target] ?? 'text/plain';
      const fileName = buildOutputName(file.name, target);
      return {
        blob: new Blob([output], { type: `${mime};charset=utf-8` }),
        fileName,
        metadata: {
          name: fileName,
          size: new TextEncoder().encode(output).length,
          type: { extension: target, mime, category: 'document' },
        },
        fidelity,
        verificationPassed: verification.passed,
        verificationDetails: verification.details,
      };
    } catch (error) {
      if (error instanceof EngineFailure) throw error;
      throw new EngineFailure(
        'MALFORMED_FILE',
        `PDF 解析失败：${error instanceof Error ? error.message : String(error)}`,
      );
    } finally {
      // 释放 pdf.js 的 Worker 与缓存，避免连续转换时内存累积。
      await Promise.resolve(document?.destroy?.()).catch(() => undefined);
    }
  }
}
